/**
 * Client-side interface to call the backend serverless endpoints for Gemini AI Grading.
 * Secures the API Key by ensuring it is never processed or bundled on the frontend.
 *
 * Implements a background retry mechanism (Max 3 attempts with 5s cooldown)
 * while the student is viewing the Heuristic Report and scrolling down.
 */

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const evaluateWithAI = async (section, data) => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const backendUrl = isLocal
        ? 'http://localhost:3002/api/evaluateTest'
        : '/.netlify/functions/evaluateTest';

    const MAX_ATTEMPTS = 3;
    const RETRY_DELAY_MS = 5000; // 5 seconds cooldown between retries

    let lastError = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
            console.log(`[Gemini API] Requesting evaluation for ${section} (Attempt ${attempt}/${MAX_ATTEMPTS})...`);

            const appSecret = process.env.REACT_APP_APP_SECRET || 'bc71273a1aba496cabc4a6022c4dfb82ea96919e383417866afb589e4f87c63a';

            const response = await fetch(backendUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-app-secret': appSecret,
                },
                body: JSON.stringify({ section, data }),
            });

            if (response.ok) {
                const result = await response.json();
                if (result && !result.error && !result.fallback) {
                    console.log(`[Gemini API] Evaluation successful on attempt ${attempt}!`);
                    return result;
                }
                lastError = new Error(result?.error || result?.details || "Backend grading indicated fallback");
            } else {
                lastError = new Error(`HTTP Error ${response.status}`);
            }
        } catch (error) {
            console.error(`[Gemini API] Attempt ${attempt} failed:`, error.message);
            lastError = error;
        }

        // If not successful and we have remaining attempts, wait 5 seconds before retrying in background
        if (attempt < MAX_ATTEMPTS) {
            console.log(`[Gemini API] Waiting 5s before attempt ${attempt + 1}...`);
            await sleep(RETRY_DELAY_MS);
        }
    }

    // After 3 failed attempts, quietly return graceful fallback structure without crashing UI
    console.warn(`[Gemini API] All ${MAX_ATTEMPTS} attempts exhausted for ${section}. Falling back quietly.`);
    const errorMsg = lastError?.message || "Evaluation unavailable";
    const isBatch = section === 'passage_batch' || section === 'speaking_batch' || section === 'objective_speaking_batch';
    const honestMessage = "Evaluation is temporarily unavailable due to high demand. Your response has been saved — please try submitting again in a minute.";

    if (isBatch) {
        const len = data?.length || data?.items?.length || 3;
        return Array(len).fill({
            error: "Evaluation Failed",
            details: errorMsg,
            score: null,
            feedback: honestMessage,
            ideal_response: null
        });
    }

    return {
        error: "Evaluation Failed",
        details: errorMsg,
        score: null,
        feedback: honestMessage,
        corrections: [],
        tone_analysis: "N/A",
        grammar_feedback: null,
        ideal_response: null
    };
};
